import { FC, useCallback, useMemo, useState } from 'react';

import { FontAwesomeIcon } from '@appcharge/shared-ui';

import Checkbox from 'components/Checkbox/Checkbox';
import { CheckbokItem } from 'components/FilterComponents/CheckList/CheckbokItem';
import { cn } from 'components/lib/utils';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  UIButton
} from 'components/ui';

export interface MultiSelectOption {
  label: string;
  value: string;
}

export interface MultiSelectProps {
  options: MultiSelectOption[];
  value: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  renderValue?: (selected: string[]) => string;
  showSelectAll?: boolean;
  selectAllLabel?: string;
  className?: string;
  buttonClassName?: string;
  iconClassName?: string;
  contentClassName?: string;
  listClassName?: string;
  disabled?: boolean;
  dataTestId?: string;
  boxDataTestId?: string;
  listDataTestId?: string;
  itemDataTestId?: string;
  showSearch?: boolean;
  searchPlaceholder?: string;
  checkboxClassName?: string;
  itemClassName?: string;
}

export const MultiSelect: FC<MultiSelectProps> = ({
  options,
  value = [],
  onChange,
  placeholder = 'Select',
  renderValue,
  showSelectAll = false,
  selectAllLabel = 'Select all',
  className,
  buttonClassName,
  iconClassName,
  contentClassName,
  listClassName,
  disabled = false,
  dataTestId,
  boxDataTestId,
  listDataTestId,
  itemDataTestId,
  showSearch = false,
  searchPlaceholder = 'Search...',
  checkboxClassName,
  itemClassName
}) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [openOrderedOptions, setOpenOrderedOptions] = useState<MultiSelectOption[]>([]);

  const displayText = useMemo(() => {
    if (!value || value.length === 0) return placeholder;
    if (renderValue) return renderValue(value);
    if (value.length === options.length) return 'All';
    if (value.length === 1) {
      return options.find((o) => o.value === value[0])?.label || value[0];
    }
    return value
      .map((v) => options.find((o) => o.value === v)?.label || v)
      .join(', ');
  }, [value, placeholder, renderValue, options]);

  const filteredOptions = useMemo(() => {
    const base = openOrderedOptions.length > 0 ? openOrderedOptions : options;
    const filtered =
      showSearch && searchTerm
        ? (() => {
            const lower = searchTerm.toLowerCase();
            return base.filter(
              (o) =>
                o.label.toLowerCase().includes(lower) ||
                o.value.toLowerCase().includes(lower)
            );
          })()
        : base;

    return {
      checked: filtered.filter((o) => value.includes(o.value)),
      unchecked: filtered.filter((o) => !value.includes(o.value))
    };
  }, [openOrderedOptions, options, searchTerm, showSearch, value]);

  const allFilteredOptions = useMemo(
    () => [...filteredOptions.checked, ...filteredOptions.unchecked],
    [filteredOptions]
  );

  const allSelected = useMemo(
    () =>
      allFilteredOptions.length > 0 &&
      allFilteredOptions.every((option) => value.includes(option.value)),
    [allFilteredOptions, value]
  );

  const handleSelectAll = useCallback(() => {
    if (allSelected) {
      const newSelection = value.filter(
        (v) => !allFilteredOptions.some((o) => o.value === v)
      );
      onChange(newSelection);
    } else {
      const valuesToAdd = allFilteredOptions
        .map((o) => o.value)
        .filter((v) => !value.includes(v));
      onChange([...value, ...valuesToAdd]);
    }
  }, [allSelected, allFilteredOptions, value, onChange]);

  const handleToggle = useCallback(
    (toggleValue: string) => {
      const newSelection = value.includes(toggleValue)
        ? value.filter((v) => v !== toggleValue)
        : [...value, toggleValue];
      onChange(newSelection);
    },
    [value, onChange]
  );

  return (
    <div className={cn('relative w-full', className)}>
      <Popover
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (next) {
            setOpenOrderedOptions([
              ...options.filter((o) => value.includes(o.value)),
              ...options.filter((o) => !value.includes(o.value))
            ]);
          } else {
            setSearchTerm('');
          }
        }}
      >
        <PopoverTrigger asChild>
          <UIButton
            variant="outline"
            disabled={disabled}
            data-testid={boxDataTestId ?? dataTestId}
            className={cn(
              'shadow-none group h-10 justify-between bg-white p-s text-base-foreground font-normal w-full hover:bg-base-accent focus:bg-base-accent',
              !value.length && 'text-base-mutedForeground',
              buttonClassName
            )}
          >
            <div className="flex items-center justify-between w-full">
              <span className="overflow-hidden text-ellipsis whitespace-nowrap">
                {displayText}
              </span>
              <FontAwesomeIcon
                icon="fa-solid fa-chevron-down"
                className={cn(
                  'text-base-foreground transition-transform group-data-[state=open]:rotate-180 text-[#404040]',
                  iconClassName
                )}
              />
            </div>
          </UIButton>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          side="bottom"
          className={cn(
            'w-[--radix-popover-trigger-width] !p-[4px] rounded-md bg-white text-base-foreground font-normal z-1000',
            contentClassName
          )}
        >
          <div className="flex flex-col text-xs">
            {showSearch && (
              <div className="border-b border-base-input">
                <input
                  autoFocus
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={searchPlaceholder}
                  className="w-full h-10 px-3 bg-base-background text-base-foreground placeholder:text-base-foreground/50 focus:outline-none outline-none text-xs"
                />
              </div>
            )}
            {showSelectAll && (
              <div className="pb-1 border-b border-base-input">
                <Checkbox
                  id="multi-select-all"
                  className="[&[data-state=checked]]:bg-indigo-500 [&[data-state=indeterminate]]:bg-indigo-500"
                  label={selectAllLabel}
                  checked={allSelected}
                  onCheckedChange={handleSelectAll}
                />
              </div>
            )}
            <div
              className={cn('flex flex-col gap-0 max-h-[200px] overflow-y-auto', listClassName)}
              data-testid={listDataTestId}
            >
              {allFilteredOptions.length > 0 ? (
                <>
                  <div className="flex flex-col gap-1">
                    {filteredOptions.checked.map((item) => (
                      <CheckbokItem
                        key={item.value}
                        item={item}
                        selectedItems={value}
                        onChange={handleToggle}
                        dataTestId={itemDataTestId}
                        checkboxClassName={checkboxClassName}
                        itemClassName={itemClassName}
                      />
                    ))}
                  </div>
                  {filteredOptions.unchecked.map((item) => (
                    <CheckbokItem
                      key={item.value}
                      item={item}
                      selectedItems={value}
                      onChange={handleToggle}
                      dataTestId={itemDataTestId}
                      checkboxClassName={checkboxClassName}
                      itemClassName={itemClassName}
                    />
                  ))}
                </>
              ) : (
                <div className="text-center text-xs text-base-foreground/50 py-2">
                  {searchTerm ? 'No results found' : 'No options available'}
                </div>
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};
